import React, { useMemo } from 'react';
import { FaNetworkWired, FaCircle } from 'react-icons/fa';
import { KavachSwitch } from '../utils/kavachTypes';

interface KavachNetworkTopologyProps {
  switches: KavachSwitch[];
  onSwitchClick?: (sw: KavachSwitch) => void;
}

const KavachNetworkTopology: React.FC<KavachNetworkTopologyProps> = ({ switches, onSwitchClick }) => {
  // Helper function to get port status from a switch
  const getPortStatus = (sw: KavachSwitch | undefined, portNum: number): string | undefined => {
    if (!sw?.ports) return undefined;
    const portKey = `port${portNum}` as keyof typeof sw.ports;
    return sw.ports[portKey]?.status;
  };

  // Helper function to calculate switch health based on port statuses
  const getSwitchHealth = (sw: KavachSwitch | undefined): 'ALL_UP' | 'ALL_DOWN' | 'PARTIAL' => {
    if (!sw?.ports) return 'ALL_DOWN';
    const portStatuses = [
      sw.ports.port1?.status,
      sw.ports.port2?.status,
      sw.ports.port3?.status,
      sw.ports.port4?.status
    ].filter(Boolean);

    if (portStatuses.length === 0) return 'ALL_DOWN';
    const upCount = portStatuses.filter(s => s === 'UP').length;
    if (upCount === portStatuses.length) return 'ALL_UP';
    if (upCount === 0) return 'ALL_DOWN';
    return 'PARTIAL';
  };

  // Group switches - use first as main, rest as converters
  const { mainSwitch, converterSwitches } = useMemo(() => {
    return {
      mainSwitch: switches[0],
      converterSwitches: switches.slice(1, 3)
    };
  }, [switches]);

  const getPortStatusColor = (status: string | undefined) => {
    if (status === 'UP') return '#28a745';
    if (status === 'DOWN') return '#dc3545';
    return '#6c757d';
  };

  const getStatusBgColor = (health: 'ALL_UP' | 'ALL_DOWN' | 'PARTIAL') => {
    if (health === 'ALL_UP') return '#27ae60';
    if (health === 'ALL_DOWN') return '#c0392b';
    return '#f39c12';
  };

  return (
    <div className="network-topology-container">
      <div className="topology-header">
        <h3><FaNetworkWired /> S to S CONNECTIVITY - STATION KAVACH UNIT</h3>
      </div>

      {/* SVG Diagram */}
      <svg viewBox="0 0 900 700" className="topology-svg">
        <defs>
          {/* Gradients */}
          <linearGradient id="componentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f8f9fa" />
            <stop offset="100%" stopColor="#e9ecef" />
          </linearGradient>
          <linearGradient id="screenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#001a00" />
            <stop offset="100%" stopColor="#003300" />
          </linearGradient>
          <linearGradient id="switchGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#34495e" />
            <stop offset="100%" stopColor="#2c3e50" />
          </linearGradient>
          {/* Arrow marker */}
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
          </marker>
        </defs>

        {/* Main Border */}
        <rect x="10" y="10" width="880" height="680" fill="#fafbfc" stroke="#333" strokeWidth="2" rx="8" />

        {/* Title Bar */}
        <rect x="10" y="10" width="880" height="40" fill="#0056b3" rx="8" />
        <rect x="10" y="35" width="880" height="15" fill="#0056b3" />
        <text x="450" y="37" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#fff">
          S to S CONNECTIVITY - STATION KAVACH UNIT
        </text>

        {/* ============ STATIONARY KAVACH UNIT SECTION ============ */}
        <rect x="30" y="60" width="310" height="150" fill="none" stroke="#0056b3" strokeWidth="1" strokeDasharray="4,2" rx="5" />
        <text x="185" y="78" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#0056b3">STATIONARY KAVACH UNIT</text>

        {/* Logic Bin 1 */}
        <g transform="translate(50, 90)">
          <rect width="100" height="105" fill="url(#componentGradient)" stroke="#555" strokeWidth="1.5" rx="4" />
          <rect x="5" y="5" width="90" height="18" fill="#0056b3" rx="2" />
          <text x="50" y="17" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#fff">LOGIC BIN</text>
          {/* LED Row */}
          <g transform="translate(15, 30)">
            <circle cx="10" cy="8" r="5" fill="#dc3545" stroke="#333" />
            <circle cx="35" cy="8" r="5" fill="#28a745" stroke="#333" />
            <circle cx="60" cy="8" r="5" fill="#ffc107" stroke="#333" />
            <text x="10" y="22" textAnchor="middle" fontSize="6" fill="#666">W</text>
            <text x="35" y="22" textAnchor="middle" fontSize="6" fill="#666">TX</text>
            <text x="60" y="22" textAnchor="middle" fontSize="6" fill="#666">RX</text>
          </g>
          {/* RJ45 Ports */}
          <g transform="translate(15, 60)">
            <rect width="30" height="18" fill="#333" stroke="#222" rx="2" />
            <rect x="40" width="30" height="18" fill="#333" stroke="#222" rx="2" />
            <rect x="2" y="3" width="26" height="12" fill="#1a1a1a" rx="1" />
            <rect x="42" y="3" width="26" height="12" fill="#1a1a1a" rx="1" />
          </g>
          <text x="50" y="98" textAnchor="middle" fontSize="7" fill="#666">LAN RJ45</text>
        </g>

        {/* Logic Bin 2 */}
        <g transform="translate(170, 90)">
          <rect width="100" height="105" fill="url(#componentGradient)" stroke="#555" strokeWidth="1.5" rx="4" />
          <rect x="5" y="5" width="90" height="18" fill="#0056b3" rx="2" />
          <text x="50" y="17" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#fff">LOGIC BIN</text>
          {/* LED Row */}
          <g transform="translate(15, 30)">
            <circle cx="10" cy="8" r="5" fill="#dc3545" stroke="#333" />
            <circle cx="35" cy="8" r="5" fill="#28a745" stroke="#333" />
            <circle cx="60" cy="8" r="5" fill="#ffc107" stroke="#333" />
            <text x="10" y="22" textAnchor="middle" fontSize="6" fill="#666">W</text>
            <text x="35" y="22" textAnchor="middle" fontSize="6" fill="#666">TX</text>
            <text x="60" y="22" textAnchor="middle" fontSize="6" fill="#666">RX</text>
          </g>
          {/* RJ45 Ports */}
          <g transform="translate(15, 60)">
            <rect width="30" height="18" fill="#333" stroke="#222" rx="2" />
            <rect x="40" width="30" height="18" fill="#333" stroke="#222" rx="2" />
            <rect x="2" y="3" width="26" height="12" fill="#1a1a1a" rx="1" />
            <rect x="42" y="3" width="26" height="12" fill="#1a1a1a" rx="1" />
          </g>
          <text x="50" y="98" textAnchor="middle" fontSize="7" fill="#666">LAN RJ45</text>
        </g>

        {/* Data Logger */}
        <g transform="translate(500, 65)">
          <rect width="360" height="140" fill="url(#componentGradient)" stroke="#555" strokeWidth="1.5" rx="4" />
          <rect x="5" y="5" width="350" height="22" fill="#0056b3" rx="2" />
          <text x="180" y="20" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#fff">DATA LOGGER</text>

          {/* LCD Screen */}
          <g transform="translate(20, 35)">
            <rect width="150" height="55" fill="url(#screenGradient)" stroke="#222" strokeWidth="2" rx="3" />
            <text x="75" y="35" textAnchor="middle" fontSize="14" fill="#00ff00" fontFamily="monospace" fontWeight="bold">ONLINE</text>
          </g>

          {/* Input LEDs */}
          <g transform="translate(190, 35)">
            <text x="25" y="10" fontSize="8" fontWeight="bold" fill="#c00">INPUT</text>
            <circle cx="10" cy="25" r="4" fill="#28a745" stroke="#333" />
            <circle cx="10" cy="40" r="4" fill="#ffc107" stroke="#333" />
            <circle cx="10" cy="55" r="4" fill="#dc3545" stroke="#333" />
            <circle cx="40" cy="25" r="4" fill="#28a745" stroke="#333" />
            <circle cx="40" cy="40" r="4" fill="#ffc107" stroke="#333" />
            <circle cx="40" cy="55" r="4" fill="#28a745" stroke="#333" />
          </g>

          {/* Power Section */}
          <g transform="translate(270, 35)">
            <text x="30" y="10" textAnchor="middle" fontSize="8" fontWeight="bold">5V POWER</text>
            <rect x="5" y="15" width="50" height="45" fill="#333" stroke="#222" rx="3" />
            <circle cx="30" cy="37" r="8" fill="#28a745" stroke="#222" />
            <text x="30" y="70" textAnchor="middle" fontSize="7" fill="#666">ON/OFF</text>
          </g>

          {/* Bottom Ports */}
          <g transform="translate(20, 100)">
            <rect width="40" height="15" fill="#333" rx="2" />
            <rect x="50" width="40" height="15" fill="#333" rx="2" />
            <rect x="100" width="50" height="15" fill="#0066cc" rx="2" />
            <text x="20" y="28" textAnchor="middle" fontSize="6">USB1</text>
            <text x="70" y="28" textAnchor="middle" fontSize="6">USB2</text>
            <text x="125" y="28" textAnchor="middle" fontSize="6">VGA</text>
          </g>

          <text x="320" y="130" textAnchor="middle" fontSize="7" fill="#666">LAN RJ45</text>
        </g>

        {/* CAT6 Cable Lines */}
        <g>
          {/* From Logic Bin 1 */}
          <line x1="100" y1="195" x2="100" y2="230" stroke="#4a90d9" strokeWidth="3" />
          <line x1="100" y1="230" x2="350" y2="230" stroke="#4a90d9" strokeWidth="3" />
          <line x1="350" y1="230" x2="350" y2="260" stroke="#4a90d9" strokeWidth="3" />

          {/* From Logic Bin 2 */}
          <line x1="220" y1="195" x2="220" y2="240" stroke="#4a90d9" strokeWidth="3" />
          <line x1="220" y1="240" x2="400" y2="240" stroke="#4a90d9" strokeWidth="3" />
          <line x1="400" y1="240" x2="400" y2="260" stroke="#4a90d9" strokeWidth="3" />

          {/* From Data Logger */}
          <line x1="780" y1="205" x2="780" y2="240" stroke="#4a90d9" strokeWidth="3" />
          <line x1="780" y1="240" x2="550" y2="240" stroke="#4a90d9" strokeWidth="3" />
          <line x1="550" y1="240" x2="550" y2="260" stroke="#4a90d9" strokeWidth="3" />

          {/* Labels */}
          <text x="160" y="225" fontSize="8" fill="#4a90d9" fontWeight="bold">CAT 6</text>
          <text x="660" y="235" fontSize="8" fill="#4a90d9" fontWeight="bold">CAT 6</text>
        </g>

        {/* ============ MAIN ETHERNET SWITCH - TEAMLINK 5080 (UP SIDE) ============ */}
        <g
          transform="translate(250, 260)"
          style={{ cursor: mainSwitch ? 'pointer' : 'default' }}
          onClick={() => mainSwitch && onSwitchClick?.(mainSwitch)}
        >
          <rect width="400" height="130" fill="url(#switchGradient)" stroke="#1a252f" strokeWidth="2" rx="6" />

          {/* Header */}
          <rect x="5" y="5" width="390" height="28" fill="#1a252f" rx="4" />
          <text x="200" y="24" textAnchor="middle" fontSize="12" fill="#ecf0f1" fontWeight="bold">
            TEAMLINK 5080 - 8 PORT ETHERNET SWITCH (UP SIDE)
          </text>

          {/* Power */}
          <text x="15" y="55" fontSize="8" fill="#bdc3c7">110V DC</text>
          <rect x="10" y="60" width="30" height="20" fill="#333" stroke="#444" rx="2" />

          {/* RJ45 Network Ports - 8 Ethernet Ports */}
          <g transform="translate(60, 45)">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((num, idx) => (
              <g key={num} transform={`translate(${idx * 40}, 0)`}>
                <rect width="32" height="35" fill="#1a1a1a" stroke="#333" strokeWidth="1" rx="2" />
                <rect x="4" y="4" width="24" height="18" fill="#0a0a0a" rx="1" />
                <circle cx="16" cy="28" r="4" fill={getPortStatusColor(getPortStatus(mainSwitch, Math.min(num, 4)))} stroke="#333" />
                <text x="16" y="48" textAnchor="middle" fontSize="7" fill="#ecf0f1">P{num}</text>
              </g>
            ))}
            <text x="160" y="60" textAnchor="middle" fontSize="8" fill="#bdc3c7">RJ45 ETHERNET PORTS (1-8)</text>
          </g>

          {/* Status Badge */}
          <rect x="10" y="100" width="90" height="22" fill={getStatusBgColor(getSwitchHealth(mainSwitch))} rx="4" />
          <text x="55" y="115" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">
            {getSwitchHealth(mainSwitch) === 'ALL_UP' ? 'All Ports UP' : getSwitchHealth(mainSwitch) === 'ALL_DOWN' ? 'Offline' : 'Partial'}
          </text>

          {/* Model Label */}
          <text x="300" y="115" textAnchor="middle" fontSize="11" fill="#ecf0f1" fontWeight="bold">8-PORT ETHERNET SWITCH</text>
        </g>

        {/* CAT6 Cable from Switch to Converters */}
        <g>
          {/* To Converter 1 (UP Side) */}
          <line x1="350" y1="390" x2="350" y2="420" stroke="#4a90d9" strokeWidth="3" />
          <line x1="350" y1="420" x2="180" y2="420" stroke="#4a90d9" strokeWidth="3" />
          <line x1="180" y1="420" x2="180" y2="455" stroke="#4a90d9" strokeWidth="3" />

          {/* To Converter 2 (DOWN Side) */}
          <line x1="550" y1="390" x2="550" y2="420" stroke="#4a90d9" strokeWidth="3" />
          <line x1="550" y1="420" x2="720" y2="420" stroke="#4a90d9" strokeWidth="3" />
          <line x1="720" y1="420" x2="720" y2="455" stroke="#4a90d9" strokeWidth="3" />

          <text x="265" y="415" fontSize="8" fill="#4a90d9" fontWeight="bold">CAT 6</text>
          <text x="635" y="415" fontSize="8" fill="#4a90d9" fontWeight="bold">CAT 6</text>
        </g>

        {/* ============ TEAMLINK 5022 - 4 PORT (2 OPTICAL + 2 ETHERNET) - DOWN SIDE ============ */}

        {/* TeamLink 5022 - Converter 1 */}
        <g
          transform="translate(50, 455)"
          style={{ cursor: converterSwitches[0] ? 'pointer' : 'default' }}
          onClick={() => converterSwitches[0] && onSwitchClick?.(converterSwitches[0])}
        >
          <rect width="280" height="100" fill="url(#switchGradient)" stroke="#1a252f" strokeWidth="2" rx="6" />

          {/* Header */}
          <rect x="5" y="5" width="270" height="22" fill="#1a252f" rx="3" />
          <text x="140" y="20" textAnchor="middle" fontSize="10" fill="#ecf0f1" fontWeight="bold">
            TEAMLINK 5022 - 4 PORT (DOWN SIDE)
          </text>

          {/* Power */}
          <text x="12" y="45" fontSize="7" fill="#bdc3c7">110V</text>
          <rect x="10" y="50" width="25" height="18" fill="#333" stroke="#444" rx="2" />

          {/* Ethernet Ports (2 ports) */}
          <g transform="translate(45, 35)">
            <text x="35" y="8" textAnchor="middle" fontSize="7" fill="#bdc3c7">ETHERNET</text>
            <g transform="translate(0, 12)">
              <rect width="30" height="28" fill="#1a1a1a" stroke="#333" rx="2" />
              <rect x="3" y="3" width="24" height="14" fill="#0a0a0a" rx="1" />
              <circle cx="15" cy="22" r="4" fill={getPortStatusColor(getPortStatus(converterSwitches[0], 1))} stroke="#333" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#bdc3c7">E1</text>
            </g>
            <g transform="translate(40, 12)">
              <rect width="30" height="28" fill="#1a1a1a" stroke="#333" rx="2" />
              <rect x="3" y="3" width="24" height="14" fill="#0a0a0a" rx="1" />
              <circle cx="15" cy="22" r="4" fill={getPortStatusColor(getPortStatus(converterSwitches[0], 2))} stroke="#333" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#bdc3c7">E2</text>
            </g>
          </g>

          {/* Optical Ports (2 ports) */}
          <g transform="translate(145, 35)">
            <text x="35" y="8" textAnchor="middle" fontSize="7" fill="#bdc3c7">OPTICAL</text>
            <g transform="translate(0, 12)">
              <rect width="30" height="28" fill="#9b59b6" stroke="#7d3c98" rx="2" />
              <circle cx="15" cy="14" r="6" fill="#2c3e50" stroke="#444" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#ecf0f1">O1</text>
            </g>
            <g transform="translate(40, 12)">
              <rect width="30" height="28" fill="#9b59b6" stroke="#7d3c98" rx="2" />
              <circle cx="15" cy="14" r="6" fill="#2c3e50" stroke="#444" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#ecf0f1">O2</text>
            </g>
          </g>

          {/* Status Badge */}
          <rect x="220" y="35" width="50" height="18" fill={getStatusBgColor(getSwitchHealth(converterSwitches[0]))} rx="3" />
          <text x="245" y="48" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">
            {getSwitchHealth(converterSwitches[0]) === 'ALL_UP' ? 'Active' : getSwitchHealth(converterSwitches[0]) === 'ALL_DOWN' ? 'Offline' : 'Partial'}
          </text>

          {/* Label */}
          <text x="240" y="72" textAnchor="middle" fontSize="8" fill="#95a5a6">2 ETH + 2 OPT</text>
        </g>

        {/* TeamLink 5022 - Converter 2 */}
        <g
          transform="translate(570, 455)"
          style={{ cursor: converterSwitches[1] ? 'pointer' : 'default' }}
          onClick={() => converterSwitches[1] && onSwitchClick?.(converterSwitches[1])}
        >
          <rect width="280" height="100" fill="url(#switchGradient)" stroke="#1a252f" strokeWidth="2" rx="6" />

          {/* Header */}
          <rect x="5" y="5" width="270" height="22" fill="#1a252f" rx="3" />
          <text x="140" y="20" textAnchor="middle" fontSize="10" fill="#ecf0f1" fontWeight="bold">
            TEAMLINK 5022 - 4 PORT (DOWN SIDE)
          </text>

          {/* Power */}
          <text x="12" y="45" fontSize="7" fill="#bdc3c7">110V</text>
          <rect x="10" y="50" width="25" height="18" fill="#333" stroke="#444" rx="2" />

          {/* Ethernet Ports (2 ports) */}
          <g transform="translate(45, 35)">
            <text x="35" y="8" textAnchor="middle" fontSize="7" fill="#bdc3c7">ETHERNET</text>
            <g transform="translate(0, 12)">
              <rect width="30" height="28" fill="#1a1a1a" stroke="#333" rx="2" />
              <rect x="3" y="3" width="24" height="14" fill="#0a0a0a" rx="1" />
              <circle cx="15" cy="22" r="4" fill={getPortStatusColor(getPortStatus(converterSwitches[1], 1))} stroke="#333" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#bdc3c7">E1</text>
            </g>
            <g transform="translate(40, 12)">
              <rect width="30" height="28" fill="#1a1a1a" stroke="#333" rx="2" />
              <rect x="3" y="3" width="24" height="14" fill="#0a0a0a" rx="1" />
              <circle cx="15" cy="22" r="4" fill={getPortStatusColor(getPortStatus(converterSwitches[1], 2))} stroke="#333" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#bdc3c7">E2</text>
            </g>
          </g>

          {/* Optical Ports (2 ports) */}
          <g transform="translate(145, 35)">
            <text x="35" y="8" textAnchor="middle" fontSize="7" fill="#bdc3c7">OPTICAL</text>
            <g transform="translate(0, 12)">
              <rect width="30" height="28" fill="#9b59b6" stroke="#7d3c98" rx="2" />
              <circle cx="15" cy="14" r="6" fill="#2c3e50" stroke="#444" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#ecf0f1">O1</text>
            </g>
            <g transform="translate(40, 12)">
              <rect width="30" height="28" fill="#9b59b6" stroke="#7d3c98" rx="2" />
              <circle cx="15" cy="14" r="6" fill="#2c3e50" stroke="#444" />
              <text x="15" y="48" textAnchor="middle" fontSize="6" fill="#ecf0f1">O2</text>
            </g>
          </g>

          {/* Status Badge */}
          <rect x="220" y="35" width="50" height="18" fill={getStatusBgColor(getSwitchHealth(converterSwitches[1]))} rx="3" />
          <text x="245" y="48" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">
            {getSwitchHealth(converterSwitches[1]) === 'ALL_UP' ? 'Active' : getSwitchHealth(converterSwitches[1]) === 'ALL_DOWN' ? 'Offline' : 'Partial'}
          </text>

          {/* Label */}
          <text x="240" y="72" textAnchor="middle" fontSize="8" fill="#95a5a6">2 ETH + 2 OPT</text>
        </g>

        {/* ============ OFC PATCH CABLES ============ */}

        {/* OFC Cable 1 - UP Side */}
        <g transform="translate(50, 570)">
          <rect width="280" height="35" fill="#f5f5f5" stroke="#999" strokeWidth="1" strokeDasharray="4,2" rx="4" />
          <line x1="15" y1="15" x2="265" y2="15" stroke="#e91e63" strokeWidth="4" strokeLinecap="round" />
          <line x1="15" y1="23" x2="265" y2="23" stroke="#2196f3" strokeWidth="4" strokeLinecap="round" />
          <text x="140" y="50" textAnchor="middle" fontSize="9" fill="#666" fontWeight="bold">OFC PATCH CABLE</text>
        </g>

        {/* OFC Cable 2 - DOWN Side */}
        <g transform="translate(570, 570)">
          <rect width="280" height="35" fill="#f5f5f5" stroke="#999" strokeWidth="1" strokeDasharray="4,2" rx="4" />
          <line x1="15" y1="15" x2="265" y2="15" stroke="#e91e63" strokeWidth="4" strokeLinecap="round" />
          <line x1="15" y1="23" x2="265" y2="23" stroke="#2196f3" strokeWidth="4" strokeLinecap="round" />
          <text x="140" y="50" textAnchor="middle" fontSize="9" fill="#666" fontWeight="bold">OFC PATCH CABLE</text>
        </g>

        {/* ============ UP SIDE / DOWN SIDE TERMINALS ============ */}

        {/* UP SIDE Terminal */}
        <g transform="translate(20, 620)">
          <rect width="50" height="55" fill="#333" stroke="#555" strokeWidth="2" rx="4" />
          <rect x="5" y="5" width="40" height="45" fill="#1a1a1a" rx="2" />
          <circle cx="25" cy="27" r="8" fill="#444" stroke="#555" />
          <line x1="25" y1="60" x2="25" y2="680" stroke="#333" strokeWidth="4" />
        </g>
        <text x="95" y="660" fontSize="14" fontWeight="bold" fill="#0056b3">← UP SIDE</text>

        {/* DOWN SIDE Terminal */}
        <g transform="translate(830, 620)">
          <rect width="50" height="55" fill="#333" stroke="#555" strokeWidth="2" rx="4" />
          <rect x="5" y="5" width="40" height="45" fill="#1a1a1a" rx="2" />
          <circle cx="25" cy="27" r="8" fill="#444" stroke="#555" />
          <line x1="25" y1="60" x2="25" y2="680" stroke="#333" strokeWidth="4" />
        </g>
        <text x="720" y="660" fontSize="14" fontWeight="bold" fill="#0056b3">DOWN SIDE →</text>

        {/* Manufacturer Logo */}
        <g transform="translate(350, 665)">
          <text x="100" y="12" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#c0392b">KERNEX MICROSYSTEMS (I) LTD.,</text>
          <text x="100" y="24" textAnchor="middle" fontSize="8" fill="#666">HYDERABAD</text>
        </g>
      </svg>

      {/* Legend */}
      <div className="topology-legend">
        <div className="legend-item">
          <FaCircle style={{ color: '#28a745', fontSize: '10px' }} />
          <span>Port UP</span>
        </div>
        <div className="legend-item">
          <FaCircle style={{ color: '#dc3545', fontSize: '10px' }} />
          <span>Port DOWN</span>
        </div>
        <div className="legend-item">
          <FaCircle style={{ color: '#6c757d', fontSize: '10px' }} />
          <span>Unknown</span>
        </div>
        <div className="legend-item">
          <div className="fiber-strand pink small"></div>
          <span>Fiber Tx</span>
        </div>
        <div className="legend-item">
          <div className="fiber-strand blue small"></div>
          <span>Fiber Rx</span>
        </div>
        <div className="legend-item">
          <div className="cable-indicator cat6"></div>
          <span>CAT6 Cable</span>
        </div>
      </div>
    </div>
  );
};

export default KavachNetworkTopology;
